<template>
  <StepItem
    format="form"
    title="Payment Details"
    subtitle="This page is encrypted for your security | Step 3 of 3"
    show-lock
    can-go-to-prev-step
    @handle-prev-step="handlePrevStep"
    @handle-next-step="handleNextStep"
    :is-ready-for-next-step="isReadyForNextStep"
  >
    <!--    Payment Processing Loader    -->
    <AnimationLoading v-if="isCreatingCompany" dark fullscreen
                      :text="`Creating ${store.name || 'Company'} • Do Not Close This Page`"/>

    <StripePaymentInfo
      ref="stripePaymentInfo"
      :billingPlan="selectedPlan?.billingPlan"
      :billingPlanIndex="selectedPlan?.index"
      :cycleInterval="store.cycleInterval"
      v-model:billingEmail="store.billingEmail"
      v-model:billingAddressLine1="store.billingAddressLine1"
      v-model:billingAddressLine2="store.billingAddressLine2"
      v-model:billingCity="store.billingCity"
      v-model:billingState="store.billingState"
      v-model:billingPostalCode="store.billingPostalCode"
      v-model:billingCountry="store.billingCountry"
      v-model:promotionCodeId="store.promotionCodeId"
      v-model:promotionCodeIsDitry="promotionCodeIsDirty"
      v-model:trialDays="store.trialDays"
      :location-count="store.locations.length"
      :validate-current-step="validateCurrentStep"
      :handle-previous-step="handlePrevStep"
      v-model:price="price"
    />

    <FormInput
      v-if="showReferralCode || store.referralCode"
      v-model="store.referralCode"
      label="Referral Code"
      placeholder="Enter Referral Code"
      class="mt-6"
    />

    <button v-else type="button" @click="showReferralCode=!showReferralCode"
            class="mt-6 flex items-center justify-start text-sm font-medium text-slate-500 space-x-2 hover:text-bdBlue-800 hover:underline">
      <PlusCircleIcon class="h-5 w-5"/>
      <span>Add Referral Code</span>
    </button>

    <template v-slot:next-step-text>{{ ctaCopy }}</template>
  </StepItem>
</template>

<script setup>
import AnimationLoading from "./AnimationLoading.vue";
import StripePaymentInfo from "./StripePaymentInfo.vue";
import StepItem from "./StepItem.vue";
import FormInput from "./FormInput.vue";
import {useCompanyAccountCreateStore} from "../stores/CompanyAccountCreateStore";
import {computed, inject, onActivated, ref, watch,} from "vue";
import {cloneDeep} from "lodash";
import {useAccountStore} from "../stores/AccountStore.js";
import {useReferrerQueryParams} from "../composables/ReferrerQueryParams.js";
import {PlusCircleIcon} from "@heroicons/vue/20/solid/index.js";

const store = useCompanyAccountCreateStore();
const accountStore = useAccountStore();

const referrerHandler = useReferrerQueryParams();

const emits = defineEmits(["complete", "prev-step"]);

const isReadyForNextStep = ref(false);
const isCreatingCompany = ref(false);
const promotionCodeIsDirty = ref(false);
const showReferralCode = ref(false);
const price = ref("$0");
const stripePaymentInfo = ref();

const cancelAsu = inject("manuallyCancelAbandonedSignUp", () => {});
const startAsu = inject("manuallyStartAbandonedSignUp", () => {});

const handlePrevStep = () => emits("prev-step", 1);

const handleNextStep = async () => {
  if (promotionCodeIsDirty.value) {
    toast.warning("Please click the 'Apply' button to apply the promotion code.");
    return;
  }

  validateCurrentStep();
  if (!isReadyForNextStep.value) return;

  isCreatingCompany.value = true;

  const paymentMethod = await stripePaymentInfo.value.getCardInfo({
    name: store.name,
  });

  if (!paymentMethod) {
    isCreatingCompany.value = false;
    return;
  }

  store.stripePaymentId = paymentMethod.id;
  store.setFurthestStep(4, "Payment Details Added");
  cancelAsu();

  await store.createCompany((wasSuccessful, error = null) => {
    isCreatingCompany.value = false;

    if (error) startAsu();

    if (wasSuccessful && !error) {
      accountStore.fetchUserAccount();
      emits("complete");
    }
  });
};

const validateCurrentStep = (notifyUserOnError) => isReadyForNextStep.value = store.validateStep7(notifyUserOnError);

/** Set up Stripe & Populate default values */
const setDefaultInfo = () => {
  store.billingEmail = cloneDeep(store.displayEmail);

  if (!store.billingAddressLine1) {
    store.billingAddressLine1 = cloneDeep(store.addressLine1);
    store.billingAddressLine2 = cloneDeep(store.addressLine2);
    store.billingCity = cloneDeep(store.city);
    store.billingState = cloneDeep(store.state);
    store.billingPostalCode = cloneDeep(store.postalCode);
    store.billingCountry = "US";
  }
  validateCurrentStep();
};

const ctaCopy = computed(() => {
  if (store.trialDays > 0) return `Start ${store.trialDays} Day Free Trial`;
  return `Pay ${price.value} & Create Company`;
});

const selectedPlan = computed(() => {
  if (!store.billingPlanId || !store.cycleInterval) return null;

  const billingPlan = store.billingPlans.find(plan => plan.id === store.billingPlanId);
  const billingPlanIndex = store.billingPlans.indexOf(billingPlan);

  if (!billingPlan) return null;

  return {
    billingPlan: billingPlan,
    index: billingPlanIndex,
  };
});

watch(() => referrerHandler.referrerCode, (newVal, oldVal) => {
  if (!newVal.value || newVal?.value === oldVal?.value) return;
  store.referralCode = newVal.value;
}, {immediate: true});

onActivated(() => {
  setDefaultInfo();
});
</script>
